import { saveReport, isReportStorageId } from "./reportHistoryApi.js";

// One recoverable draft per authenticated user and brand in this browser tab.
// The store owns pending writes so changing routes cannot discard their outcome.
const stores = new Map();
const record = value => !!value && typeof value === "object" && !Array.isArray(value);
const validDay = value => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0,10) === value;
const validPeriod = value => record(value) && validDay(value.start) && validDay(value.end) && value.start <= value.end;
const metricKeys = ["spend","revenue","cv","impressions","clicks","roas","ctr","cvr","cpc","cpa","cpm","aov"];
const textKeys = ["date","start","end","name","media","source","account_id","campaign_id","campaign_name","adset_id","adset_name","ad_id","ad_name","ad_channel","type","text","category","keyword_text","match_type"];
const scalarText = value => value == null || typeof value === "string" || typeof value === "number";
const metricValue = value => value == null || (typeof value === "number" && Number.isFinite(value)) || (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value)));
const validRow = value => record(value) && textKeys.every(key => scalarText(value[key])) && metricKeys.every(key => metricValue(value[key]));
const rows = value => value == null || (Array.isArray(value) && value.every(validRow));
const keywordSummary = value => value == null || (record(value) && rows(value.top) && rows(value.bottom) && ["poolCount","totalCount"].every(key => value[key] == null || (Number.isSafeInteger(value[key]) && value[key] >= 0)));
const stringList = value => value == null || (Array.isArray(value) && value.every(item => typeof item === "string"));
const groupSettings = value => record(value) && typeof value.name === "string" && scalarText(value.id) && scalarText(value.viewType) && stringList(value.campaignKeys);
const validPerformance = value => record(value) && validRow(value.totals) &&
  (value.prev_totals == null || validRow(value.prev_totals)) &&
  ["by_media","by_date","campaigns","creatives","daily","weekly","top_campaigns","top_creatives","by_creative","by_adset","by_date_detail","daily_by_media","status_changes"].every(key => rows(value[key])) &&
  (value.meta == null || (record(value.meta) && stringList(value.meta.media_list))) &&
  (value.coverage == null || (record(value.coverage) && stringList(value.coverage.observed_dates) && stringList(value.coverage.previous_observed_dates)));
export function isRenderableReportDraft(draft) {
  const data = draft?.reportData;
  return record(draft) && isReportStorageId(draft.timestamp) && record(data) &&
    ["daily","weekly","monthly"].includes(data.type) && validPeriod(data.period) &&
    validPerformance(data.totalPerformance) && Array.isArray(data.groups) &&
    data.groups.every(group => groupSettings(group) && validPerformance(group.performance) && keywordSummary(group.keywords)) &&
    (draft.groups == null || (Array.isArray(draft.groups) && draft.groups.every(groupSettings))) && rows(draft.timeline);
}
let activePrincipal = "";
export function setReportDraftPrincipal(principal) {
  activePrincipal = String(principal || "").trim().toLowerCase();
  syncUnloadProtection();
}
// The draft owner outlives route components, including a draft that could not fit in storage.
const hasDrafts = () => [...stores.values()].some(store => { const state = store.getSnapshot(); return store.principal === activePrincipal && state && state.phase !== "saved"; });
const warnBeforeUnload = event => { if (hasDrafts()) { event.preventDefault(); event.returnValue = ""; } };
let unloadTarget = null;
function syncUnloadProtection() {
  const target = globalThis.window;
  if (hasDrafts() && target?.addEventListener && !unloadTarget) {
    target.addEventListener("beforeunload", warnBeforeUnload); unloadTarget = target;
  } else if (!hasDrafts() && unloadTarget) {
    unloadTarget.removeEventListener("beforeunload", warnBeforeUnload); unloadTarget = null;
  }
}
export function getReportDraftStore(principal, brandSlug, storage) {
  const scope = `${String(principal || "").trim().toLowerCase()}:${brandSlug}`;
  if (stores.has(scope)) return stores.get(scope);
  if (storage === undefined) { try { storage = globalThis.sessionStorage; } catch { storage = null; } }
  const key = `mediaplex_report_draft_v1:${scope}`;
  const listeners = new Set();
  let state = null;
  try {
    const saved = principal && JSON.parse(storage?.getItem(key) || "null");
    if (saved?.version === 1 && isRenderableReportDraft(saved.draft))
      state = { phase: "failed", slug: brandSlug, draft: saved.draft, pending: false, cached: true };
  } catch { /* Server history remains available if local storage is unreadable. */ }
  const emit = (next) => { state = next; syncUnloadProtection(); for (const listener of listeners) listener(); };
  const cache = (draft) => {
    try {
      if (!principal || !storage) return false;
      storage.setItem(key, JSON.stringify({ version: 1, draft }));
      return true;
    } catch { return false; }
  };
  const store = {
    principal: String(principal || "").trim().toLowerCase(),
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot() { return state; },
    stage(draft) {
      emit({ phase: "dirty", slug: brandSlug, draft, pending: state?.pending || false, cached: cache(draft) });
    },
    clearSaved() { if (state?.phase === "saved") emit(null); },
    discard() {
      if (state?.pending) return false;
      try { storage?.removeItem(key); } catch { return false; }
      emit(null); return true;
    },
    loaded(draft) {
      if (!isRenderableReportDraft(draft) || (state && state.phase !== "saved")) return false;
      emit({ phase: "saved", slug: brandSlug, draft, pending: false, cached: false });
      return true;
    },
    async save(draft = state?.draft) {
      if (!draft || state?.pending) return;
      emit({ phase: "saving", slug: brandSlug, draft, pending: true, cached: cache(draft) });
      try {
        await saveReport(brandSlug, draft);
        if (state.draft === draft) {
          try { storage?.removeItem(key); } catch { /* A retained draft can be saved idempotently. */ }
          emit({ ...state, phase: "saved", pending: false });
        } else emit({ ...state, pending: false }); // A newer edit still needs its own receipt.
      } catch {
        emit({ ...state, phase: state.draft === draft ? "failed" : "dirty", pending: false });
      }
    },
  };
  stores.set(scope, store);
  syncUnloadProtection();
  return store;
}
